const SITE_URL = 'https://suarte.art';
const DEFAULT_PROFILE_IMAGE = 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/personalEmailSignature';
const PROFILE_ROUTE_DESCRIPTION_TEMPLATES = {
  'profile-gallery-artworks': 'Explore {publicArtworkCount} by {publicProfileName} on Suarte. Discover original contemporary art from this public gallery profile.',
  'profile-gallery-artists': 'Meet {representedArtistCount} from {publicProfileName} on Suarte and explore their public artworks.',
  'profile-gallery-board-posts': "Read {publishedPostCount} from {publicProfileName} on Suarte and follow this gallery's public updates.",
  'profile-gallery-board-articles': "Read {publishedArticleCount} from {publicProfileName} on Suarte and explore this gallery's public editorial content.",
  'profile-artist-artworks': "Explore {publicArtworkCount} by {publicProfileName} on Suarte and discover this artist's public body of work.",
  'profile-artist-collection': "Explore {publicCollectionArtworkCount} from {publicProfileName}'s collection on Suarte.",
  'profile-artist-community-thoughts': 'Read {publicThoughtCount} from {publicProfileName} on Suarte and follow their perspective on art.',
  'profile-artist-community-ratings': 'Browse {publicRatingCount} from {publicProfileName} on Suarte and discover their visible reactions to art.',
  'profile-artist-artlists': 'Play {indexableArtlistCount} curated by {publicProfileName} on Suarte, each with at least 5 public artworks.',
  'profile-artist-board-posts': "Read {publishedPostCount} from {publicProfileName} on Suarte and follow this artist's public updates.",
  'profile-artist-board-articles': "Read {publishedArticleCount} from {publicProfileName} on Suarte and explore this artist's editorial content.",
  'profile-collector-collection': "Explore {publicCollectionArtworkCount} from {publicProfileName}'s collection on Suarte.",
  'profile-collector-community-thoughts': 'Read {publicThoughtCount} from {publicProfileName} on Suarte and follow their perspective on art.',
  'profile-collector-community-ratings': 'Browse {publicRatingCount} from {publicProfileName} on Suarte and discover their visible reactions to art.',
  'profile-collector-artlists': 'Play {indexableArtlistCount} curated by {publicProfileName} on Suarte, each with at least 5 public artworks.',
  'profile-collector-board-posts': "Read {publishedPostCount} from {publicProfileName} on Suarte when this collector's public board is active.",
  'profile-collector-board-articles': "Read {publishedArticleCount} from {publicProfileName} on Suarte when this collector's public board is active.",
};

function normalizeString(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function truncateText(value, maxLength = 160) {
  const normalized = normalizeString(value);
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const shortened = normalized.slice(0, Math.max(0, maxLength - 1)).trim();
  const lastSpaceIndex = shortened.lastIndexOf(' ');
  const safeValue = lastSpaceIndex > 80 ? shortened.slice(0, lastSpaceIndex) : shortened;
  return `${safeValue.trim()}...`;
}

function appendImageVariant(imageUrl, variant = 'w=1200') {
  const normalized = normalizeString(imageUrl);
  if (!normalized) {
    return DEFAULT_PROFILE_IMAGE;
  }

  const alreadyVariant = /\/[a-z0-9=_-]+$/i.test(normalized);
  if (alreadyVariant) {
    return normalized;
  }

  return normalized.endsWith('/') ? `${normalized}${variant}` : `${normalized}/${variant}`;
}

function resolveProfileType(data = {}) {
  return normalizeString(data?.user_type || data?.__t).toLowerCase();
}

function resolveProfileName(data = {}) {
  const profileType = resolveProfileType(data);
  if (profileType === 'collector') {
    return normalizeString(data?.user_username) || 'Collector';
  }

  return normalizeString(data?.user_name) || normalizeString(data?.user_username) || 'Suarte Profile';
}

function resolveUsername(data = {}) {
  return normalizeString(data?.user_username);
}

function resolveImage(data = {}) {
  return appendImageVariant(data?.user_image?.image_url, 'w=1200');
}

function normalizeCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function pluralize(label, count) {
  return `${count} ${count === 1 ? label : `${label}s`}`;
}

function resolveProfileRoutePolicyId(profileType, subPage, options = {}) {
  const normalizedType = normalizeString(profileType).toLowerCase();
  const boardView = normalizeString(options.boardView).toLowerCase();
  const communityView = normalizeString(options.communityView).toLowerCase();

  if (!normalizedType) {
    return null;
  }

  if (subPage === 'artworks' && ['gallery', 'artist'].includes(normalizedType)) {
    return `profile-${normalizedType}-artworks`;
  }

  if (subPage === 'artists' && normalizedType === 'gallery') {
    return 'profile-gallery-artists';
  }

  if (subPage === 'collection' && ['artist', 'collector'].includes(normalizedType)) {
    return `profile-${normalizedType}-collection`;
  }

  if (subPage === 'artlists' && ['artist', 'collector'].includes(normalizedType)) {
    return `profile-${normalizedType}-artlists`;
  }

  if (subPage === 'community' && ['artist', 'collector'].includes(normalizedType)) {
    return communityView === 'ratings'
      ? `profile-${normalizedType}-community-ratings`
      : `profile-${normalizedType}-community-thoughts`;
  }

  if (subPage === 'board' && ['gallery', 'artist', 'collector'].includes(normalizedType)) {
    return boardView === 'articles'
      ? `profile-${normalizedType}-board-articles`
      : `profile-${normalizedType}-board-posts`;
  }

  return null;
}

function buildProfileRouteTemplateVariables(data = {}, options = {}) {
  const safePrimaryCount = normalizeCount(options.itemCount);
  const safeSecondaryCount = normalizeCount(options.secondaryCount);

  return {
    publicProfileName: resolveProfileName(data),
    publicArtworkCount: pluralize('artwork', safePrimaryCount),
    representedArtistCount: pluralize('represented artist', safePrimaryCount),
    publicCollectionArtworkCount: pluralize('publicly shared artwork', safePrimaryCount),
    publicThoughtCount: pluralize('public thought', safePrimaryCount),
    publicRatingCount: pluralize('public rating', safeSecondaryCount),
    indexableArtlistCount: pluralize('public artlist', safePrimaryCount),
    publishedPostCount: pluralize('published post', safePrimaryCount),
    publishedArticleCount: pluralize('published article', safePrimaryCount),
  };
}

function applyDescriptionTemplate(template, variables) {
  const normalizedTemplate = normalizeString(template);
  if (!normalizedTemplate) {
    return '';
  }

  return truncateText(
    normalizedTemplate.replace(/\{([a-zA-Z0-9]+)\}/g, (_match, token) => {
      const value = variables?.[token];
      return typeof value === 'string' && value.trim() ? value.trim() : '';
    })
  );
}

function applyTitleTemplate(template, variables) {
  return applyDescriptionTemplate(template, variables);
}

function buildDefaultProfileRouteDescription(data = {}, policyId, options = {}) {
  const template = PROFILE_ROUTE_DESCRIPTION_TEMPLATES[policyId];
  if (!template) {
    return '';
  }

  return applyDescriptionTemplate(
    template,
    buildProfileRouteTemplateVariables(data, options)
  );
}

function isPublicProfileEligible(data = {}) {
  const profileType = resolveProfileType(data);
  const username = resolveUsername(data).toLowerCase();

  if (!username || username === 'suarte') {
    return false;
  }

  if (!data?.user_flags?.onboarding_completed) {
    return false;
  }

  if (profileType === 'gallery' && data?.gallery_closed) {
    return false;
  }

  return ['gallery', 'artist', 'collector'].includes(profileType);
}

function formatCountLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function buildBreadcrumbItems(name, canonicalUrl, subPage, boardView) {
  const items = [
    { name: 'Home', url: SITE_URL },
    { name, url: canonicalUrl.replace(/\/(artworks|artists|collection|artlists|community|board)(\?.*)?$/, '') },
  ];

  const labels = {
    artworks: 'Artworks',
    artists: 'Artists',
    collection: 'Collection',
    artlists: 'Artlists',
    community: 'Community',
    board: boardView === 'articles' ? 'Articles' : 'Posts',
  };

  items.push({ name: labels[subPage] || 'Profile', url: canonicalUrl });
  return items;
}

function buildSchema({ title, description, canonicalUrl, image, name, pageType, profileType, subPage, itemCount }) {
  const entityType = profileType === 'gallery' ? 'Organization' : 'Person';

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': pageType,
        '@id': `${canonicalUrl}#page`,
        name: title,
        description,
        url: canonicalUrl,
        primaryImageOfPage: image,
        about: {
          '@id': `${canonicalUrl}#profile`,
        },
      },
      {
        '@type': entityType,
        '@id': `${canonicalUrl}#profile`,
        name,
        image,
        url: canonicalUrl.replace(/\/(artworks|artists|collection|artlists|community|board)(\?.*)?$/, ''),
      },
      itemCount > 0 ? {
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#list`,
        name: `${name} ${subPage}`,
        numberOfItems: itemCount,
      } : null,
    ].filter(Boolean),
  };
}

export function buildProfileSubpageSeoMetadata(data = {}, options = {}) {
  const {
    subPage,
    internal = false,
    pathname = '',
    search = '',
    boardView = 'posts',
    communityView = 'thoughts',
    itemCount = 0,
    secondaryCount = 0,
  } = options;

  const profileType = resolveProfileType(data);
  const name = resolveProfileName(data);
  const username = resolveUsername(data);
  const image = resolveImage(data);
  const safePrimaryCount = normalizeCount(itemCount);
  const safeSecondaryCount = normalizeCount(secondaryCount);
  const defaultCanonicalPath = internal
    ? pathname || '/profile'
    : `/user/${username}${subPage === 'board' && boardView === 'articles'
      ? '/board?view=articles'
      : `/${subPage}`}`;
  const canonicalUrl = `${SITE_URL}${defaultCanonicalPath}`;
  const publicProfileEligible = isPublicProfileEligible(data);
  const routeKey = `${pathname}${search || ''}`;

  const policyId = resolveProfileRoutePolicyId(profileType, subPage, { boardView, communityView });
  let title = `${name} on Suarte`;
  let description = policyId
    ? buildDefaultProfileRouteDescription(data, policyId, {
      itemCount: safePrimaryCount,
      secondaryCount: safeSecondaryCount,
    }) || `Explore ${name}'s public profile on Suarte.`
    : `Explore ${name}'s public profile on Suarte.`;
  let pageType = 'CollectionPage';
  let indexable = false;

  switch (subPage) {
  case 'artworks':
    title = `${name}'s Artworks on Suarte`;
    description = safePrimaryCount > 0
      ? truncateText(`Explore ${formatCountLabel(safePrimaryCount, 'artwork')} by ${name} on Suarte. Discover original contemporary art from this public profile.`)
      : truncateText(`Explore artworks by ${name} on Suarte.`);
    indexable = ['gallery', 'artist'].includes(profileType) && safePrimaryCount > 0;
    break;
  case 'artists':
    title = `${name}'s Artists on Suarte`;
    description = safePrimaryCount > 0
      ? truncateText(`Meet ${formatCountLabel(safePrimaryCount, 'represented artist')} from ${name} on Suarte and explore their public artworks.`)
      : truncateText(`Meet the represented artists of ${name} on Suarte.`);
    indexable = profileType === 'gallery' && safePrimaryCount > 0;
    break;
  case 'collection':
    title = `${name}'s Collection on Suarte`;
    description = safePrimaryCount > 0
      ? truncateText(`Explore ${formatCountLabel(safePrimaryCount, 'publicly shared artwork')} from ${name}'s collection on Suarte.`)
      : truncateText(`Explore ${name}'s collection on Suarte.`);
    indexable = safePrimaryCount > 0;
    break;
  case 'artlists':
    title = `${name}'s Artlists on Suarte`;
    description = safePrimaryCount > 0
      ? truncateText(`Play ${formatCountLabel(safePrimaryCount, 'curated artlist')} by ${name} on Suarte and discover their public selections.`)
      : truncateText(`Play curated artlists by ${name} on Suarte.`);
    indexable = safePrimaryCount > 0;
    break;
  case 'community': {
    const communityCount = safePrimaryCount + safeSecondaryCount;
    title = `${name}'s Community on Suarte`;
    description = communityCount > 0
      ? truncateText(`Read ${formatCountLabel(safePrimaryCount, 'thought')} and ${formatCountLabel(safeSecondaryCount, 'rating')} from ${name} on Suarte.`)
      : truncateText(`Explore ${name}'s public community activity on Suarte.`);
    indexable = communityCount > 0;
    break;
  }
  case 'board': {
    const label = boardView === 'articles' ? 'Articles' : 'Posts';
    title = `${name}'s ${label} on Suarte`;
    description = safePrimaryCount > 0
      ? truncateText(`Read ${formatCountLabel(safePrimaryCount, `published ${boardView === 'articles' ? 'article' : 'post'}`)} from ${name} on Suarte.`)
      : truncateText(`Read public ${boardView} from ${name} on Suarte.`);
    indexable = safePrimaryCount > 0;
    pageType = 'WebPage';
    break;
  }
  default:
    break;
  }

  const overrideTemplate = policyId ? data?.__seoProfileRouteOverrides?.[policyId] : '';
  const overrideTitleTemplate = policyId ? data?.__seoProfileRouteTitleOverrides?.[policyId] : '';
  if (overrideTitleTemplate) {
    title = applyTitleTemplate(
      overrideTitleTemplate,
      buildProfileRouteTemplateVariables(data, {
        itemCount: safePrimaryCount,
        secondaryCount: safeSecondaryCount,
      })
    ) || title;
  }
  if (overrideTemplate) {
    description = applyDescriptionTemplate(
      overrideTemplate,
      buildProfileRouteTemplateVariables(data, {
        itemCount: safePrimaryCount,
        secondaryCount: safeSecondaryCount,
      })
    ) || description;
  }

  const backendRobots = data?.__seoProfileSubpageRobots?.routeKey === routeKey
    ? data.__seoProfileSubpageRobots.robots
    : '';
  const robots = backendRobots || (
    internal || !publicProfileEligible || !indexable
      ? 'noindex,follow,max-image-preview:large'
      : 'index,follow,max-image-preview:large'
  );

  return {
    title,
    description,
    image,
    canonicalUrl,
    robots,
    pageType,
    breadcrumbItems: buildBreadcrumbItems(name, canonicalUrl, subPage, boardView),
    structuredData: buildSchema({
      title,
      description,
      canonicalUrl,
      image,
      name,
      pageType,
      profileType,
      subPage: boardView === 'articles' ? 'articles' : subPage,
      itemCount: safePrimaryCount,
    }),
  };
}
